import React, { useState } from "react";
import { twMerge } from "tailwind-merge";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { useSelector } from "react-redux";
import { motion } from "framer-motion";
import {
  selectCodeStatus,
  selectCodesGrid,
  selectIsGameOver,
} from "store/game/selectors";
import "./CodeHistoryGrid.css";
import CodeGridItem from "./CodeGridItem";
import ItemDescription from "../ProductGrid/GridSlotControls/ItemDescription";

const gridContainerVariants = {
  hidden: { opacity: 1 },
  visible: {
    opacity: 1,
    transition: {
      delayChildren: 0.3,
      staggerChildren: 0.005,
    },
  },
};

const unMappedChapters = Array(97)
  .fill(null)
  .map((_, index) => {
    const i = (index + 1).toLocaleString("en-US", {
      minimumIntegerDigits: 2,
      useGrouping: false,
    });
    return i;
  });

export default function CodeHistoryGrid() {
  const codes = useSelector(selectCodesGrid);
  const isGameOver = useSelector(selectIsGameOver);
  const [history, setHistory] = useState([]);

  const [visibleGrid, setVisibleGrid] = useState(() =>
    unMappedChapters.map((chapter) => {
      if (codes[chapter]) {
        return {
          code: chapter,
          description: codes[chapter]?.description,
          children: codes[chapter]?.children,
        };
      }

      return chapter;
    })
  );

  const codeStatus = useSelector(selectCodeStatus);

  const handleClick = (item) => {
    if (item?.code === "back") {
      let prevItems = history.pop();
      setVisibleGrid(prevItems);
      setHistory([...history]);
    } else {
      setHistory([...history, visibleGrid]);
      setVisibleGrid(
        Object.keys(item?.children).map((key) => {
          return {
            code: key,
            description: item?.children[key]?.description,
            children: item?.children[key]?.children,
          };
        })
      );
    }
  };

  return (
    <motion.ul
      key={history.length}
      initial="hidden"
      animate="visible"
      variants={gridContainerVariants}
      className="codes-grid"
    >
      <CodeGridItem
        className={history.length > 0 && "cursor-pointer bg-amber-400"}
        opacity={history.length === 0 ? 0 : 1}
        onClick={() => {
          if (history.length > 0) {
            handleClick({ code: "back" });
          }
        }}
      >
        <FontAwesomeIcon icon={faArrowLeft} />
      </CodeGridItem>

      {Array.isArray(visibleGrid) &&
        visibleGrid.map((item, index) => {
          const hasChildren = Object.keys(item?.children || {}).length > 0;
          const code = typeof item === "string" ? item : item?.code;

          return (
            <ItemDescription text={isGameOver && item?.description} key={code}>
              <CodeGridItem
                opacity={hasChildren ? 1 : 0.6}
                className={twMerge(
                  "h-full w-full",
                  item?.description && "border-2",
                  hasChildren && "cursor-pointer bg-indigo-800"
                )}
                status={codeStatus[code]}
                key={`${code}-${index}`}
                onClick={() => {
                  if (hasChildren) {
                    handleClick(item);
                  }
                }}
              >
                {code}
              </CodeGridItem>
            </ItemDescription>
          );
        })}
    </motion.ul>
  );
}
